import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cargomatic/ui';
import Pagination from 'components/v2/PaginationV2';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import WebAPIUtils, {
  getLaneRatesWithRateConditions,
} from 'utils/AccessorialRequestWebAPIUtils';
import { laneRatesMapper } from './lane-rates-v2-helpers';
import moment from 'moment-timezone';
import SessionStore from 'stores/SessionStore';
import StatusBadge from './StatusBadge';
import { Pencil } from 'lucide-react';
import { NO_DATA } from 'utils/constants';

const AUDITS_PER_PAGE = 10;

function Field({ label, value }) {
  return (
    <div className="mb-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value ?? NO_DATA}</dd>
    </div>
  );
}

function formatDate(date) {
  if (!date || !moment(date).isValid()) return NO_DATA;
  return moment(date).utc().format('YYYY-MM-DD');
}

function formatCurrency(value) {
  if (value == null) return NO_DATA;
  return `$${parseFloat(value).toFixed(2)}`;
}

function getOriginDisplay(laneRate) {
  return (
    laneRate.originZoneName ||
    laneRate.originCity ||
    laneRate.originZip ||
    NO_DATA
  );
}

function getDestinationDisplay(laneRate) {
  return (
    laneRate.destinationZoneName ||
    laneRate.destinationCity ||
    laneRate.destinationZip ||
    NO_DATA
  );
}

function formatRateConditions(rateConditions) {
  if (!rateConditions || rateConditions.length === 0) return null;

  const conditionLabels = {
    customer: 'Customer',
    orderType: 'Order Type',
    equipmentType: 'Equipment Type',
    equipmentSize: 'Equipment Size',
    hazardous: 'Hazardous',
    steamshipOrRailroad: 'Steamship/Railroad',
  };

  return rateConditions.map((rc) => ({
    label: conditionLabels[rc.type] || rc.type,
    value: rc.type === 'hazardous' ? (rc.criteria ? 'Yes' : 'No') : rc.criteria,
    meta: rc.meta,
  }));
}

function getAuditColumns(t) {
  return [
    {
      accessorKey: 'message',
      header: t('NAME'),
      cell: ({ getValue }) => (
        <div className="text-sm">{getValue() || NO_DATA}</div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: t('TIMESTAMP'),
      cell: ({ getValue }) => (
        <div className="text-sm whitespace-nowrap">
          {getValue() ? moment(getValue()).format('LLL') : NO_DATA}
        </div>
      ),
    },
    {
      accessorKey: 'user',
      header: t('SOURCE'),
      cell: ({ getValue }) => (
        <div className="text-sm">{getValue() || 'CSV Upload'}</div>
      ),
    },
  ];
}

function LaneRateViewCard({ laneRate, index, total, defaultTab }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(defaultTab || 'details');
  const [audits, setAudits] = useState([]);
  const [auditsCount, setAuditsCount] = useState(0);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [auditPagination, setAuditPagination] = useState({
    pageIndex: 0,
    pageSize: AUDITS_PER_PAGE,
  });

  const auditColumns = useMemo(() => getAuditColumns(t), [t]);

  const isShipperLanePricingManager =
    SessionStore.isShipperLanePricingManager();
  const isCarrierLanePricingManager =
    SessionStore.isCarrierLanePricingManager();

  const fetchAudits = useCallback(async (laneRateId, pageIndex) => {
    setAuditsLoading(true);
    try {
      const skip = pageIndex * AUDITS_PER_PAGE;
      const params = new URLSearchParams({
        lanerateId: laneRateId,
        per_page: AUDITS_PER_PAGE,
        skip,
      });
      const response = await WebAPIUtils.getUserLaneRateAudits(
        params.toString(),
      );
      if (response) {
        setAudits(response.audits || []);
        setAuditsCount(response.count || 0);
      }
    } finally {
      setAuditsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'audit' && laneRate?.id) {
      fetchAudits(laneRate.id, auditPagination.pageIndex);
    }
  }, [activeTab, laneRate?.id, auditPagination.pageIndex, fetchAudits]);

  const auditTable = useReactTable({
    columns: auditColumns,
    data: audits,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    rowCount: auditsCount,
    state: {
      pagination: auditPagination,
    },
    onPaginationChange: setAuditPagination,
  });

  function canEdit() {
    if (!laneRate) return false;
    if (isShipperLanePricingManager && isCarrierLanePricingManager) return true;
    if (isShipperLanePricingManager && laneRate.customerType === 'Shipper')
      return true;
    if (isCarrierLanePricingManager && laneRate.customerType === 'Carrier')
      return true;
    return false;
  }

  function handleEditClick() {
    history.push(`/lane-rates/${laneRate.id}/edit`);
  }

  const rateConditions = formatRateConditions(laneRate.rateConditions);
  const title = total > 1 ? `${t('LANE_RATE')} ${index + 1}` : t('LANE_RATE');

  return (
    <Card>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between mb-4">
            <CardTitle>{title}</CardTitle>
            {canEdit() && (
              <Button variant="outline" onClick={handleEditClick}>
                <Pencil className="w-4 h-4 mr-2" />
                {t('EDIT_LANE_RATE')}
              </Button>
            )}
          </div>
          <TabsList className="justify-start">
            <TabsTrigger value="details">{t('DETAILS')}</TabsTrigger>
            <TabsTrigger value="audit">{t('AUDIT_TRAIL')}</TabsTrigger>
          </TabsList>
        </CardHeader>

        <TabsContent value="details" className="mt-0">
          <CardContent>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              {t('LANE_DETAILS')}
            </h3>
            <dl className="sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-x-4">
              <Field
                label={`${t('CARRIER')}/${t('SHIPPER')}`}
                value={laneRate.customerName}
              />
              <Field label={t('CUSTOMER_TYPE')} value={laneRate.customerType} />
              <Field label={t('ORIGIN')} value={getOriginDisplay(laneRate)} />
              <Field label={t('ORIGIN_STATE')} value={laneRate.originState} />
              <Field
                label={t('DESTINATION')}
                value={getDestinationDisplay(laneRate)}
              />
              <Field
                label={t('DESTINATION_STATE')}
                value={laneRate.destinationState}
              />
              <Field
                label={t('REVERSIBLE')}
                value={
                  laneRate.reversible === true
                    ? t('YES')
                    : laneRate.reversible === false
                      ? t('NO')
                      : NO_DATA
                }
              />
              <Field
                label={t('STATUS')}
                value={
                  laneRate.status ? (
                    <StatusBadge status={laneRate.status} />
                  ) : (
                    NO_DATA
                  )
                }
              />
            </dl>

            <hr className="my-6" />
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              {t('RATE_DETAILS')}
            </h3>
            <dl className="sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-x-4">
              <Field
                label={t('RATE', { count: 1 })}
                value={formatCurrency(laneRate.rate)}
              />
              <Field label={t('QUOTE_ID')} value={laneRate.quoteId} />
              <Field label={t('NOTES')} value={laneRate.notes} />
            </dl>

            {rateConditions && rateConditions.length > 0 && (
              <>
                <hr className="my-6" />
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  {t('RATE_CONDITIONS')}
                </h3>
                <dl className="sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-x-4">
                  {rateConditions.map((rc, i) => (
                    <Field
                      key={i}
                      label={rc.label}
                      value={rc.meta?.name || rc.value}
                    />
                  ))}
                </dl>
              </>
            )}

            <hr className="my-6" />
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              {t('VALIDITY_PERIOD')}
            </h3>
            <dl className="sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-x-4">
              <Field
                label={t('VALIDITY_START_DATE')}
                value={formatDate(laneRate.effectiveStart)}
              />
              <Field
                label={t('VALIDITY_END_DATE')}
                value={formatDate(laneRate.effectiveEnd)}
              />
              <Field
                label={t('WEEKLY_COMMITMENT')}
                value={laneRate.weeklyCommitment ?? laneRate.commitment}
              />
            </dl>

            <hr className="my-6" />
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              {t('DISTANCE_CONFIGURATION')}
            </h3>
            <dl className="sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-x-4">
              <Field
                label={t('BILLING_DISTANCE')}
                value={laneRate.billingDistance}
              />
              <Field
                label={t('BILL_ROUNDTRIP')}
                value={
                  laneRate.billRoundtrip === true
                    ? t('YES')
                    : laneRate.billRoundtrip === false
                      ? t('NO')
                      : NO_DATA
                }
              />
            </dl>
          </CardContent>
        </TabsContent>

        <TabsContent value="audit" className="mt-0">
          <CardContent className="space-y-4">
            <DataTable table={auditTable} isLoading={auditsLoading} />
            {auditsCount > AUDITS_PER_PAGE && (
              <Pagination
                page={auditPagination.pageIndex + 1}
                perPage={AUDITS_PER_PAGE}
                total={auditsCount}
                onPageClick={(page) =>
                  setAuditPagination((prev) => ({
                    ...prev,
                    pageIndex: page - 1,
                  }))
                }
              />
            )}
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
}

export function LaneRateView() {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams();
  const [laneRates, setLaneRates] = useState([]);
  const [loading, setLoading] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const defaultTab = searchParams.get('tab') === 'audit' ? 'audit' : 'details';

  useEffect(() => {
    async function fetchData() {
      try {
        const query = id ? `?id=${id}` : location.search;
        const result = await getLaneRatesWithRateConditions(query);
        const data = result.data;
        const mapped = await laneRatesMapper(data.laneRates);
        setLaneRates(mapped);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="w-full">
        <Card>
          <CardHeader className="pb-3">
            <Skeleton className="h-6 w-32" />
            <div className="flex gap-2 mt-4">
              <Skeleton className="h-9 w-20" />
              <Skeleton className="h-9 w-24" />
            </div>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-24 mb-4" />
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i}>
                  <Skeleton className="h-3 w-20 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
            <hr className="my-6" />
            <Skeleton className="h-4 w-24 mb-4" />
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}>
                  <Skeleton className="h-3 w-20 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (laneRates.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          {t('LANE_RATE_NOT_FOUND')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full space-y-6">
      {laneRates.map((laneRate, i) => (
        <LaneRateViewCard
          key={laneRate.id || i}
          laneRate={laneRate}
          index={i}
          total={laneRates.length}
          defaultTab={defaultTab}
        />
      ))}
    </div>
  );
}
